import React, { useState } from 'react';
import { XIcon, VideoIcon, FileTextIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Asset } from '../pages/AssetsManagement';

interface AssetDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: Asset | null;
}

const AssetDetailModal: React.FC<AssetDetailModalProps> = ({ isOpen, onClose, asset }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !asset) return null;

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'image': return 'Image';
      case 'video': return 'Video';
      case 'pdf': return 'PDF';
      default: return 'File';
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(asset.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy link');
    }
  };

  const renderPreview = () => {
    if (asset.type === 'image' && (asset.thumbnailUrl || asset.url)) {
      return (
        <img
          src={asset.thumbnailUrl || asset.url}
          alt={asset.name}
          className="w-full max-h-80 object-contain rounded bg-gray-50 border"
        />
      );
    }
    if (asset.type === 'video') {
      return (
        <div className="w-full h-48 bg-gray-900 rounded flex items-center justify-center">
          <VideoIcon className="h-12 w-12 text-white opacity-60" />
        </div>
      );
    }
    if (asset.type === 'pdf') {
      return (
        <div className="w-full h-48 bg-red-50 rounded flex items-center justify-center">
          <FileTextIcon className="h-12 w-12 text-red-400" />
        </div>
      );
    }
    return (
      <div className="w-full h-48 bg-gray-100 rounded flex items-center justify-center">
        <FileTextIcon className="h-12 w-12 text-gray-400" />
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl max-h-[90vh] flex flex-col rounded shadow-xl mx-4">
        {/* Header */}
        <div className="px-8 pt-8 pb-4 flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Preview Asset</h2>
            <p className="mt-1 text-sm text-gray-500">View the details and information associated with this asset.</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-8 pb-4 space-y-6">
          {/* Preview */}
          {renderPreview()}

          {/* Asset details */}
          <div className="bg-gray-50 rounded">
            <div className="px-4 py-3">
              <p className="text-sm font-semibold text-gray-900">Asset details</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm"><span className="font-semibold text-gray-900">Name : </span><span className="text-gray-600">{asset.name}</span></p>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm"><span className="font-semibold text-gray-900">Type : </span><span className="text-gray-600">{getTypeLabel(asset.type)}</span></p>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm"><span className="font-semibold text-gray-900">Size: </span><span className="text-gray-600">{formatFileSize(asset.size)}</span></p>
            </div>
            <div className="px-4 py-3">
              <p className="text-sm"><span className="font-semibold text-gray-900">Update date: </span><span className="text-gray-600">{formatDate(asset.uploadDate)}</span></p>
            </div>
            <div className="px-4 py-3">
              <div className="flex items-start gap-2">
                <p className="text-sm break-all"><span className="font-semibold text-gray-900">{getTypeLabel(asset.type)} URL: </span><span className="text-gray-600">{asset.url}</span></p>
                <button
                  onClick={handleCopyLink}
                  className="flex-shrink-0 text-sm text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1"
                >
                  {copied ? <><CheckIcon className="h-3.5 w-3.5" /> Copied</> : <><CopyIcon className="h-3.5 w-3.5" /> Copy link</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded hover:bg-indigo-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssetDetailModal;
